// src/comparisons/ComparisonDashboard.js
import { useState, useEffect } from 'react';
import {
  Box,
  Paper,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  IconButton,
  CircularProgress,
  Alert,
  Tooltip,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from '@mui/material';
import {
  Visibility as ViewIcon,
  Refresh as RefreshIcon,
} from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import api from '../config/api';
import useStore from '../config/store';
import ComparisonResults from './ComparisonResult';

const ComparisonDashboard = () => {
  const { t } = useTranslation();
  const project = useStore((state) => state.project);
  const { authToken } = useStore.getState();

  const [comparisons, setComparisons] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [selectedComparison, setSelectedComparison] = useState(null);
  const [modalOpen, setModalOpen] = useState(false);

  const fetchComparisons = async () => {
    if (!project) {
      setComparisons([]);
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const response = await api.get('/comparisons/by-project', {
        params: { projectId: project },
        headers: { Authorization: `Bearer ${authToken}` },
      });

      if (response.data?.errorCode === 0) {
        const data = response.data?.response?.data || [];
        const sorted = [...data].sort((a, b) => {
          const dateA = a.created_at?._seconds || 0;
          const dateB = b.created_at?._seconds || 0;
          return dateB - dateA;
        });
        setComparisons(sorted);
      } else {
        setError(t('comparisonDashboard.reportsLoadError'));
      }
    } catch (err) {
      console.error('Error al cargar historial de comparaciones:', err);
      setError(t('comparisonDashboard.reportsLoadError'));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchComparisons();
  }, [project]);

  const formatDate = (timestamp) => {
    if (!timestamp?._seconds) return t('dateNotAvailable');
    const date = new Date(timestamp._seconds * 1000);
    return date.toLocaleDateString('es-ES', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const getStatusChip = (status) => {
    const statusMap = {
      completado: { color: 'success', label: t('status.completado') },
      procesando: { color: 'warning', label: t('status.procesando') },
      pendiente: { color: 'default', label: t('comparisonDashboard.statusPendiente') },
      error: { color: 'error', label: t('status.error') },
      cancelado: { color: 'default', label: t('comparisonDashboard.statusCancelado') },
    };
    const config = statusMap[status] || { color: 'default', label: status };
    return <Chip label={config.label} color={config.color} size="small" />;
  };

  const handleViewComparison = (comparison) => {
    setSelectedComparison(comparison);
    setModalOpen(true);
  };

  const handleCloseModal = () => {
    setModalOpen(false);
    setSelectedComparison(null);
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" sx={{ py: 6 }}>
        <CircularProgress size={32} sx={{ mr: 2 }} />
        <Typography color="text.secondary">{t('processing')}</Typography>
      </Box>
    );
  }

  if (error) {
    return (
      <Box sx={{ mt: 2 }}>
        <Alert
          severity="error"
          action={
            <Button color="inherit" size="small" onClick={fetchComparisons}>
              {t('retry')}
            </Button>
          }
        >
          {error}
        </Alert>
      </Box>
    );
  }

  if (comparisons.length === 0) {
    return (
      <Box sx={{ mt: 6, textAlign: 'center' }}>
        <Typography variant="h2" sx={{ mb: 2 }}>📋</Typography>
        <Typography variant="h6" color="text.secondary" gutterBottom>
          {t('comparisonDashboard.noHistoryFound')}
        </Typography>
        <Typography variant="body2" color="text.secondary">
          {t('comparisonDashboard.noHistoryFoundDesc')}
        </Typography>
      </Box>
    );
  }

  return (
    <Box>
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 3 }}>
        <Typography variant="body1" color="text.secondary">
          {t('comparisonDashboard.totalComparisons', { count: comparisons.length })}
        </Typography>
        <Tooltip title={t('comparisonDashboard.refresh')}>
          <IconButton onClick={fetchComparisons} size="small">
            <RefreshIcon />
          </IconButton>
        </Tooltip>
      </Box>

      <TableContainer component={Paper} sx={{ borderRadius: 2, boxShadow: '0 4px 20px rgba(0,0,0,0.08)' }}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell sx={{ fontWeight: 'bold' }}>
                {t('comparisonDashboard.targetPosition')}
              </TableCell>
              <TableCell sx={{ fontWeight: 'bold' }}>
                {t('comparisonDashboard.comparisonMode')}
              </TableCell>
              <TableCell sx={{ fontWeight: 'bold' }} align="center">
                {t('comparisonDashboard.applicantsAnalized')}
              </TableCell>
              <TableCell sx={{ fontWeight: 'bold' }}>
                {t('createdAt')}
              </TableCell>
              <TableCell sx={{ fontWeight: 'bold' }} align="center">
                {t('statusWord')}
              </TableCell>
              <TableCell sx={{ fontWeight: 'bold' }} align="center">
                {t('actions')}
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {comparisons.map((comparison) => (
              <TableRow
                key={comparison.id}
                hover
                sx={{ '&:last-child td, &:last-child th': { border: 0 } }}
              >
                <TableCell>
                  <Typography variant="body2" fontWeight="medium">
                    {comparison.position_name || '—'}
                  </Typography>
                </TableCell>
                <TableCell>
                  <Chip
                    label={
                      comparison.comparison_type === '1vsAll'
                        ? t('comparisonDashboard.1vsAll')
                        : t('comparisonDashboard.allVsAll')
                    }
                    size="small"
                    variant="outlined"
                    color="primary"
                  />
                </TableCell>
                <TableCell align="center">
                  <Typography variant="body2">
                    {comparison.result?.comparison_summary?.total_candidates ||
                      Object.keys(comparison.selected_reports || {}).length ||
                      '—'}
                  </Typography>
                </TableCell>
                <TableCell>
                  <Typography variant="body2" color="text.secondary">
                    {formatDate(comparison.created_at)}
                  </Typography>
                </TableCell>
                <TableCell align="center">
                  {getStatusChip(comparison.status)}
                </TableCell>
                <TableCell align="center">
                  <Tooltip
                    title={
                      comparison.status !== 'completado'
                        ? t('comparisonDashboard.viewResultsDisabled')
                        : t('comparisonDashboard.viewResults')
                    }
                  >
                    <span>
                      <IconButton
                        size="small"
                        color="primary"
                        onClick={() => handleViewComparison(comparison)}
                        disabled={comparison.status !== 'completado'}
                      >
                        <ViewIcon />
                      </IconButton>
                    </span>
                  </Tooltip>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Modal para ver resultados */}
      <Dialog
        open={modalOpen}
        onClose={handleCloseModal}
        maxWidth="lg"
        fullWidth
        PaperProps={{ sx: { borderRadius: 2, maxHeight: '90vh' } }}
      >
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <Typography variant="h6">
            🏆 {t('comparisonDashboard.comparisonResults')}
          </Typography>
          {selectedComparison && (
            <Chip
              label={selectedComparison.position_name}
              color="primary"
              size="small"
            />
          )}
        </DialogTitle>
        <DialogContent dividers>
          {selectedComparison && (
            <ComparisonResults
              comparison={selectedComparison}
              loading={false}
              error={null}
              onRetry={null}
              onNewComparison={null}
            />
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseModal} variant="outlined">
            {t('cancel')}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default ComparisonDashboard;